import React, { useState } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import ToPeso from "../components/misc/ToPeso";
import { format, addMonths, addWeeks, addDays, addYears, parseISO } from "date-fns";
import { calculatePenalty } from '../utils/loanCalculations';

const roundUp = (amount) => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) {
    return 0;
  }
  
  const amountString = numAmount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);
  if (secondDecimalPlace !== "0") {
    return parseFloat((numAmount + 0.01).toFixed(2));
  }
  return numAmount;
};

const calculateDueDates = (startDate, interval, term, amount, interest, adv) => {
  const dueDates = [];
  const principalAmount = amount / term;
  const interestAmountInitial = (adv)?(amount * interest) / 100:0;
  const interestAmountMinusAdvance = (((amount * interest) / 100) * term) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / term;
  
  let currentDate = parseISO(startDate);

  for (let i = 0; i < term; i++) {
    switch (interval.toLowerCase()) {
      case 'monthly':
        currentDate = addDays(currentDate, 31);
        break;
      case 'bimonthly': // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case 'weekly':
        currentDate = addWeeks(currentDate, 1);
        break;
      case 'quarterly':
        currentDate = addMonths(currentDate, 3);
        break;
      case 'semiannual':
        currentDate = addMonths(currentDate, 6);
        break;
      case 'yearly':
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error('Invalid interval');
    }

    dueDates.push({
      date: format(currentDate, 'MM/dd/yyyy'),
      dueAmount: roundUp(principalAmount + interestAmount)
    });
  }

  return dueDates;
};

function DocumentRePastdueAccountFinalNoticeBeforeLegalAction({ applicantDetails, loanProcessingDetails }) {
  const [modalShow, setModalShow] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [formData, setFormData] = useState({
    principalBalance: "",
    interestBalance: "",
    penaltyBalance: "",
    pdiBalance: "",
    otherChargesBalance: "25.00",  // Default to 25.00 for the transfer fee
  });

  if (!applicantDetails) {
    return null;
  }

  const handleModalOpen = (loan) => {
    setSelectedLoan(loan);
    
    try {
      // Calculate the payment schedule
      const dates = calculateDueDates(
        loan.dateGranted, 
        loan.loanInterval, 
        loan.loanTerm, 
        parseFloat(loan.amountGranted), 
        parseFloat(loan.interestRate), 
        loan.advanceInterest
      );
      
      // Get current date
      const currentDate = new Date();
      
      // Ensure payments is an array
      const payments = Array.isArray(loan.payments) ? loan.payments : [];

      console.log("Payments:", payments);
      
      // Calculate total principal and interest amounts from the loan details
      const totalPrincipal = parseFloat(loan.amountGranted);
      const interestRate = parseFloat(loan.interestRate) / 100;
      const loanTerm = parseInt(loan.loanTerm, 10);
      
      const totalInterest = totalPrincipal * interestRate * loanTerm;
      
      // Calculate totals from payment history
      const totalPaidPrincipal = payments.reduce((sum, payment) => sum + parseFloat(payment.paidPrincipal || 0), 0);
      const totalPaidInterest = payments.reduce((sum, payment) => sum + parseFloat(payment.paidInterest || 0), 0);
      const totalPaidPenalty = payments.reduce((sum, payment) => sum + parseFloat(payment.paidPenalty || 0), 0);
      const totalPaidOther = payments.reduce((sum, payment) => sum + parseFloat(payment.paidOther || 0), 0);
      
      // Calculate remaining balances
      // For principal, deduct both paidPrincipal and paidOther
      let principalBalance = totalPrincipal - totalPaidPrincipal - totalPaidOther;
      // Ensure it's not negative
      principalBalance = Math.max(0, principalBalance);
      
      // For interest, deduct paid interest
      let interestBalance = totalInterest - totalPaidInterest;
      // Ensure it's not negative
      interestBalance = Math.max(0, interestBalance);
      
      // Calculate penalty using the utility function
      const penaltyAmount = calculatePenalty(dates, payments, currentDate);
      
      // Set the form data with the calculated values
      setFormData({
        principalBalance: principalBalance.toFixed(2),
        interestBalance: interestBalance.toFixed(2),
        penaltyBalance: penaltyAmount.toFixed(2),
        pdiBalance: "0.00",
        otherChargesBalance: "25.00", // Transfer fee
      });
      
      // Log for debugging
      console.log("Calculated form data:", {
        totalPrincipal,
        totalInterest,
        totalPaidPrincipal,
        totalPaidInterest,
        totalPaidPenalty,
        totalPaidOther,
        principalBalance: principalBalance.toFixed(2),
        interestBalance: interestBalance.toFixed(2),
        penaltyBalance: penaltyAmount.toFixed(2),
      });
    } catch (error) {
      console.error("Error calculating amounts:", error);
      
      // Provide default values in case of error
      setFormData({
        principalBalance: "0.00",
        interestBalance: "0.00",
        penaltyBalance: "0.00",
        pdiBalance: "0.00",
        otherChargesBalance: "25.00", // Still set the transfer fee
      });
    }
    
    setModalShow(true);
  };

  const handleModalClose = () => {
    setModalShow(false);
    setSelectedLoan(null);
    setFormData({
      principalBalance: "",
      interestBalance: "",
      penaltyBalance: "",
      pdiBalance: "",
      otherChargesBalance: "25.00",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    if (Object.values(formData).some((value) => !value)) {
      alert("All fields are required.");
      return;
    }

    if (!selectedLoan) return;

    const schedule = calculateDueDates(
      selectedLoan.dateGranted,
      selectedLoan.loanInterval,
      selectedLoan.loanTerm,
      parseFloat(selectedLoan.amountGranted),
      parseFloat(selectedLoan.interestRate),
      selectedLoan.advanceInterest
    );

    const existingPdfBytes = await fetch("/pdftemplates/re-pastdue-account-final-notice-before-legal-action.pdf").then(res =>
      res.arrayBuffer()
    );

    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    firstPage.drawText(`${applicantDetails.firstName.toUpperCase()} ${applicantDetails.middleName.toUpperCase() || ""} ${applicantDetails.lastName.toUpperCase()}`, {
      x: 37,
      y: 640,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    firstPage.drawText(`${applicantDetails.street.toUpperCase()}, ${applicantDetails.barangay.toUpperCase()},`, {
        x: 37,
        y: 628,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${applicantDetails.municipality.toUpperCase()}, ${applicantDetails.province.toUpperCase()} ${applicantDetails.zipCode.toUpperCase()}`, {
        x: 37,
        y: 619,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
    });

    const today = new Date();
    const day = today.getDate();
    const month = today.toLocaleString('default', { month: 'long' });
    const year = today.getFullYear();
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });

    firstPage.drawText(`${dayName}, ${month} ${day}, ${year}`, {
      x: 37,
      y: 665,
      size: 9,
      font: helveticaFont, 
      color: rgb(0, 0, 0)
    });

    let lastScheduleDate;
    try {
      if (schedule && schedule.length > 0) {
        if (schedule[schedule.length - 1].amortizationDate) {
          lastScheduleDate = schedule[schedule.length - 1].amortizationDate;
        } else if (schedule[schedule.length - 1].date) {
          lastScheduleDate = schedule[schedule.length - 1].date;
        } else {
          const endDateEstimate = new Date(selectedLoan.dateGranted);
          endDateEstimate.setMonth(endDateEstimate.getMonth() + parseInt(selectedLoan.loanTerm));
          lastScheduleDate = format(endDateEstimate, "MM/dd/yyyy");
        }
      } else {
        const endDateEstimate = new Date(selectedLoan.dateGranted);
        endDateEstimate.setMonth(endDateEstimate.getMonth() + parseInt(selectedLoan.loanTerm));
        lastScheduleDate = format(endDateEstimate, "MM/dd/yyyy");
      }
    } catch (error) {
      console.error("Error determining last schedule date:", error);
      lastScheduleDate = format(new Date(selectedLoan.dateGranted), "MM/dd/yyyy");
    }

    firstPage.drawText(`${parseFloat(selectedLoan.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { 
      x: 120, 
      y: 467, 
      size: 9, 
      font: helveticaFont, 
      color: rgb(0, 0, 0) 
    });
    
    firstPage.drawText(`${format(new Date(selectedLoan.dateGranted), "MM/dd/yyyy")}`, { 
      x: 120, 
      y: 455, 
      size: 9, 
      font: helveticaFont, 
      color: rgb(0, 0, 0) 
    });
    
    firstPage.drawText(`${lastScheduleDate}`, { 
      x: 120, 
      y: 443, 
      size: 9, 
      font: helveticaFont, 
      color: rgb(0, 0, 0) 
    });

    const totalDue = 
      parseFloat(formData.principalBalance) + 
      parseFloat(formData.interestBalance) + 
      parseFloat(formData.penaltyBalance) + 
      parseFloat(formData.pdiBalance) + 
      parseFloat(formData.otherChargesBalance);

    firstPage.drawText(`${parseFloat(formData.principalBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 467, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.interestBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 454, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.penaltyBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 442, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.pdiBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 430, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.otherChargesBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 418, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${totalDue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 228, y: 532, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${totalDue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 405, y: 380, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });

    const pdfBytes = await pdfDoc.save();
    saveAs(new Blob([pdfBytes]), `Re_Pastdue_Account_Final_Notice_Before_Legal_Action_${applicantDetails.firstName}_${applicantDetails.lastName}_${selectedLoan.id}.pdf`);
    handleModalClose();
  };

  return (
    <>
      <h3>Applicant Details</h3>
      <Table className="list-container" hover responsive>
        <tbody>
          <tr>
            <td><strong>Full Name</strong></td>
            <td>{`${applicantDetails.firstName} ${applicantDetails.middleName || ""} ${applicantDetails.lastName}`}</td>
          </tr>
          <tr>
            <td><strong>Address</strong></td>
            <td>{`${applicantDetails.street}, ${applicantDetails.barangay}, ${applicantDetails.municipality}, ${applicantDetails.province}`}</td>
          </tr>
        </tbody>
      </Table>

      <h3>Loan Processing Details</h3>
      {loanProcessingDetails.length > 0 ? (
        <Table className="list-container" hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount Granted</th>
              <th>Date Granted</th>
              <th>Interest Rate</th>
              <th>Loan Term</th>
              <th>Payment Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loanProcessingDetails.map((loan, index) => (
              <tr key={index}>
                <td>{loan.loanProcessingType}</td>
                <td><ToPeso amount={parseFloat(loan.amountGranted).toFixed(2)} /></td>
                <td>{format(new Date(loan.dateGranted), "MM/dd/yyyy")}</td>
                <td>{loan.interestRate}%</td>
                <td>{loan.loanTerm} months</td>
                <td>{loan.paymentType}</td>
                <td>
                  <Button variant="primary" onClick={() => handleModalOpen(loan)}>
                    Generate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No loan processing details found for this applicant.</p>
      )}

      <Modal 
        show={modalShow} 
        onHide={handleModalClose}
        backdrop="static" // Prevent closing by clicking outside
        keyboard={false} // Prevent closing with escape key
      >
        <Modal.Header closeButton>
          <Modal.Title>Additional Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Principal Balance</Form.Label>
              <Form.Control 
                type="number" 
                name="principalBalance" 
                value={formData.principalBalance} 
                onChange={handleInputChange} 
                step="0.01"
                required 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Interest Balance</Form.Label>
              <Form.Control 
                type="number" 
                name="interestBalance" 
                value={formData.interestBalance} 
                onChange={handleInputChange} 
                step="0.01"
                required 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Penalty Balance</Form.Label>
              <Form.Control 
                type="number" 
                name="penaltyBalance" 
                value={formData.penaltyBalance} 
                onChange={handleInputChange} 
                step="0.01"
                required 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>PDI Balance</Form.Label>
              <Form.Control 
                type="number" 
                name="pdiBalance" 
                value={formData.pdiBalance} 
                onChange={handleInputChange} 
                step="0.01"
                required 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Other Charges Balance (Transfer Fee)</Form.Label>
              <Form.Control 
                type="number" 
                name="otherChargesBalance" 
                value={formData.otherChargesBalance} 
                onChange={handleInputChange} 
                step="0.01"
                required 
              />
            </Form.Group>
            <div className="alert alert-info mt-3">
              <strong>Total Amount Due:</strong> ₱{
                (parseFloat(formData.principalBalance || 0) +
                parseFloat(formData.interestBalance || 0) +
                parseFloat(formData.penaltyBalance || 0) +
                parseFloat(formData.pdiBalance || 0) +
                parseFloat(formData.otherChargesBalance || 0)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              <div className="small mt-2">
                <i className="fas fa-info-circle me-1"></i>
                Transfer fee of ₱25.00 is included in the total.
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Generate PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DocumentRePastdueAccountFinalNoticeBeforeLegalAction;
